import { FeedbackType } from "types/feedback";
import { Requirement } from "types/requirement";
import { REQUIREMENT } from "helpers/constants";

/**
 * Finds the parent ITER requirement that contains a given competency requirement_id
 */
export const findParentITER = (
  competencyRequirementId: string,
  allRequirements: Requirement[]
): Requirement | null => {
  for (const req of allRequirements) {
    if (req.type === REQUIREMENT.REQUIREMENT_ITER && req.competencies) {
      const hasCompetency = req.competencies.some(
        (c: any) => c.competency?.requirement_id === competencyRequirementId
      );
      if (hasCompetency) {
        return req;
      }
    }
  }
  return null;
};

/**
 * Gets the requirement display data for a feedback
 * Returns ITER name if it's an ITER feedback, otherwise returns the requirement name
 */
export const getRequirementDisplayData = (
  feedback: FeedbackType,
  allRequirements: Requirement[] = []
) => {
  if (!feedback.feedback_skills?.length) {
    return { name: "No requirement", isITER: false, iterData: null };
  }

  // Check if all feedback_skills are competencies (indicating ITER)
  const allAreCompetencies = feedback.feedback_skills.every(
    (skill) => skill.requirement?.type === REQUIREMENT.REQUIREMENT_COMPETENCY
  );

  // ITER feedbacks include multiple competency skills; a single COMPETENCY is not ITER.
  if (allAreCompetencies && feedback.feedback_skills.length > 1) {
    // Find parent ITER
    const firstCompetencyId = feedback.feedback_skills[0]?.requirement?.requirement_id;
    if (firstCompetencyId && allRequirements.length > 0) {
      const parentITER = findParentITER(firstCompetencyId, allRequirements);
      if (parentITER) {
        const subRequirements = feedback.feedback_skills.map((skill) => ({
          requirement_id: skill.requirement?.requirement_id || "",
          name: skill.requirement?.name || "",
          description: skill.requirement?.description || null,
        }));
        return {
          name: parentITER.name,
          isITER: true,
          iterData: {
            iterName: parentITER.name,
            subRequirements,
          },
        };
      }
    }
    // Fallback if parent ITER not found or allRequirements not available
    return {
      name: "ITER",
      isITER: true,
      iterData: {
        iterName: "ITER",
        subRequirements: feedback.feedback_skills.map((skill) => ({
          requirement_id: skill.requirement?.requirement_id || "",
          name: skill.requirement?.name || "",
          description: skill.requirement?.description || null,
        })),
      },
    };
  }

  // For non-ITER, return the first requirement name
  const firstName = feedback.feedback_skills[0]?.requirement?.name || "No requirement";
  return { name: firstName, isITER: false, iterData: null };
};

export const getFeedbackTypeLabel = (
  feedback: FeedbackType | { feedback_skills?: any[] },
  allRequirements: Requirement[] = [],
) => {
  const skills = feedback.feedback_skills || [];
  if (skills.length === 0) return "";

  const display = getRequirementDisplayData(
    feedback as FeedbackType,
    allRequirements,
  );
  if (display.isITER) return "ITER";

  const firstType = skills
    .map((skill) => skill.requirement?.type)
    .find((type: string | undefined) => type);
  if (firstType === REQUIREMENT.REQUIREMENT_EPA) return "EPA";
  if (firstType === REQUIREMENT.REQUIREMENT_COMPETENCY) return "Competencies";
  if (firstType === REQUIREMENT.REQUIREMENT_ITER) return "ITER";
  return "";
};

